import { memo } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import cn from 'classnames';
import LinkWrapper from 'components/Common/LinkWrapper';
import { NAV_LINKS, SVG_IMAGES_TYPES } from 'utils/constants';
import { isHasSubSmallNavigation } from 'helpers/navigation';
import { routes } from 'utils/routes';
import Svg from 'UI/components/Svg';
import useToggle from 'hooks/useToggle';
import SearchInputModal from 'components/Common/GlobalSearch/SearchInputModal';
import { useNav } from './useNav';
import styles from './styles.module.scss';

const SmallDropDownMenu = dynamic(() => import('UI/sections/Header/SmallDropDownMenu'));

const Nav = (props) => {
  const {
    theme,
    navRef,
    isPageScrolling,
    links,
    isHeader,
    handleOnClick,
    isSmallDropMenuOpened,
    closeSmallDropDownMenu,
    selectedSlug,
    toggleMenu,
    disableSmallDropMenuAnimation,
    handleNavMouseEnter,
    handleNavMouseLeave,
  } = useNav(props);

  const [isFullscreenSearch, toggleFullscreenSearch] = useToggle(false);

  return (
    <>
      <ul
        className={cn(styles.desktopMenu, { [styles.pageScrolled]: isPageScrolling })}
        ref={navRef}
        onMouseEnter={handleNavMouseEnter}
        onMouseLeave={handleNavMouseLeave}
      >
        {links?.map(({
          title,
          path,
          dynamicPath,
          slug,
        }) => {
          const itemContent = (
            <span className={styles.underline}>
              {title}
            </span>
          );

          return (path || isHeader) && (
            <li
              key={`menuItem/${title}`}
              className={cn(styles[theme], { [styles.nonClickableItem]: !path })}
              onClick={handleOnClick(path)}
              onMouseEnter={() => toggleMenu({ slug, isOpen: true })}
              onMouseLeave={() => toggleMenu({ slug, isOpen: false })}
            >
              {path
                ? (
                  <LinkWrapper
                    isLocalLink
                    path={path}
                    dynamicRouting={dynamicPath}
                  >
                    {itemContent}
                  </LinkWrapper>
                )
                : itemContent}
              {isHasSubSmallNavigation(slug) && isHeader && (
                <SmallDropDownMenu
                  slug={slug}
                  selected={selectedSlug}
                  isDropMenuOpened={isSmallDropMenuOpened}
                  isPageScrolledDown={isPageScrolling}
                  closeDropDownMenu={closeSmallDropDownMenu}
                  theme={theme}
                  disableAnimation={disableSmallDropMenuAnimation}
                />
              )}
            </li>
          );
        })}
      </ul>
      <div className={cn(styles.contactUsContainer, { [styles.pageScrolled]: isPageScrolling })}>
        <Svg
          type={SVG_IMAGES_TYPES.searchLg}
          handleOnClick={toggleFullscreenSearch}
          className={cn(styles[theme], styles.searchIcon)}
        />
        <li
          className={cn(styles[theme], styles.contactUs)}
          onClick={handleOnClick(routes.contact.slug)}
        >
          <LinkWrapper
            isLocalLink
            path={routes.contact.path}
            dynamicRouting={routes.contact.dynamicPath}
          >
            <span className={styles.underline}>
              {routes.contact.title}
            </span>
          </LinkWrapper>
        </li>
      </div>
      <SearchInputModal
        isFullscreenSearch={isFullscreenSearch}
        closeFullscreenSearch={toggleFullscreenSearch}
      />
    </>
  );
};

Nav.defaultProps = {
  theme: 'dark',
  navLinks: NAV_LINKS,
  isHeader: false,
};

Nav.propTypes = {
  isPageScrolling: PropTypes.bool.isRequired,
  theme: PropTypes.string,
  navLinks: PropTypes.instanceOf(Array),
  isHeader: PropTypes.bool,
};

export default memo(Nav);
